import React, { useContext, useMemo, useState } from 'react';
import { BinContext } from '../../../../../../../context/BinContext';
import { BinStatusContext } from '../../../../../../../context/BinStatusContext';
import { CustomFieldContext } from '../../../../../../../context/CustomFieldContext';
import { TagRelationContext } from '../../../../../../../context/TagRelationContext';
import { Bin } from '../../../../../../../types/bin';
import { StockLocation } from '../../../../../../../types/stock_location/stockLocation';
import { TranslationKey } from '../../../../../../../types/translation/TranslationKey';
import { translate } from '../../../../../../../types/translation/Translator';
import { BinFilter } from '../../../../../../../VentoryUI/components/filters/BinFilter';
import { CreateButton, ImportIconButton } from '../../../../../../components/Button';
import { SearchFilter } from '../../../../../../components/Filter';
import Table from '../../../../../../components/Table';
import BinStatusLabel from '../../../../../Common/BinStatusLabel';
import BinImportModal from './BinImportModal';
import BinModal from './BinModal';
import { CompanyContext } from '../../../../../../../context/CompanyContext';
import { StockLocationContext } from '../../../../../../../context/StockLocationContext';
import BinDeleteModal from './BinDeleteModal';
import { DeleteIconButton } from '../../../../../../icons';

interface StockLocationBinPaneProps {
  stockLocation: StockLocation;
}

interface ModalProps {
  open: boolean;
  bin: Bin | undefined;
}

export default function StockLocationBinPane({ stockLocation }: StockLocationBinPaneProps) {
  const { binStatuses } = useContext(BinStatusContext);
  const { tagRelations } = useContext(TagRelationContext);
  const { bins } = useContext(BinContext);
  const { customFields } = useContext(CustomFieldContext);
  const { currentCompany } = useContext(CompanyContext);
  const { stockLocations } = useContext(StockLocationContext);

  const allItems = useMemo(() => {
    return [...bins.values()].filter(b => b.stockLocationId === stockLocation.id);
  }, [stockLocation, bins]);

  const [items, setItems] = useState(allItems);
  const [modalProps, setModalProps] = useState<ModalProps>({ open: false, bin: undefined });
  const [deleteModalProps, setDeleteModalProps] = useState<ModalProps>({ open: false, bin: undefined });
  const [importModalOpen, setImportModalOpen] = useState(false);

  const filter = new BinFilter(binStatuses, tagRelations, customFields, bins, currentCompany, stockLocations);

  return (
    <>
      {modalProps.open && (
        <BinModal
          open={modalProps.open}
          setOpen={open => setModalProps({ ...modalProps, open })}
          stockLocation={stockLocation}
          bin={modalProps.bin}
        />
      )}
      {deleteModalProps.open && deleteModalProps.bin && (
        <BinDeleteModal
          open={deleteModalProps.open}
          setOpen={open => setDeleteModalProps({ ...deleteModalProps, open })}
          bin={deleteModalProps.bin!}
        />
      )}

      {importModalOpen && <BinImportModal open={importModalOpen} setOpen={setImportModalOpen} />}
      <div className='flex flex-grow flex-col gap-4'>
        <div className='flex justify-between gap-4'>
          <SearchFilter filter={filter} items={allItems} setItems={setItems} />
          <div className='flex gap-2'>
            <ImportIconButton onClick={() => setImportModalOpen(true)} />
            <CreateButton
              text={translate({ key: TranslationKey.createBin })}
              onClick={() => setModalProps({ open: true, bin: undefined })}
            />
          </div>
        </div>
        <Table
          items={items}
          onClick={bin => setModalProps({ open: true, bin })}
          headers={[
            { key: 'name', name: translate({ key: TranslationKey.name }), text: item => item.name },
            {
              key: 'status',
              name: translate({ key: TranslationKey.status }),
              text: item => <BinStatusLabel status={binStatuses.get(item.binStatusId)} />,
            },
            {
              key: 'delete',
              name: '',
              text: item => <DeleteIconButton onClick={() => setDeleteModalProps({ open: true, bin: item })} />,
            },
          ]}
          filter={filter}
        />
      </div>
    </>
  );
}
